import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSuperHit } from '../contexts/SuperHitContext';
import { formatTwilioBalance, getEliteAiModels, hasBedrockAccess, isAiApiHit } from '../utils/superHit';
import { soundSystem } from '../utils/soundSystem';
import './SuperHitPopup.css';

const SuperHitPopup: React.FC = () => {
  const { showSuperHitPopup, currentSuperHit, closeSuperHitPopup } = useSuperHit();

  useEffect(() => {
    if (showSuperHitPopup && currentSuperHit) {
      soundSystem.playSuperHit();
    }
  }, [showSuperHitPopup, currentSuperHit]);

  if (!showSuperHitPopup || !currentSuperHit) return null;

  const metrics: string[] = [];
  const vr = currentSuperHit.verificationResult;

  if (vr?.ses?.Quota > 200) {
    metrics.push(`AWS SES — ${vr.ses.Quota} emails/jour`);
  }
  if (vr?.RemainingQuota > 200) {
    metrics.push(`SendGrid — ${vr.RemainingQuota} emails restants`);
  }
  if (vr?.EmailCredits > 200) {
    metrics.push(`Brevo — ${vr.EmailCredits} email credits`);
  }
  if (vr?.SmsCredits > 200) {
    metrics.push(`Brevo — ${vr.SmsCredits} SMS credits`);
  }
  if (currentSuperHit.services.includes('SMTP')) {
    metrics.push('SMTP — serveur de mail fonctionnel');
  }
  if (currentSuperHit.services.includes('Stripe') && vr?.Balance != null) {
    const stripeBalance = parseFloat(String(vr.Balance));
    if (!Number.isNaN(stripeBalance) && stripeBalance > 0) {
      metrics.push(`Stripe — ${vr.Balance} ${(vr.Currency as string) || 'USD'}`);
    }
  }
  if (currentSuperHit.services.includes('Twilio')) {
    const twilioBalance = formatTwilioBalance(currentSuperHit);
    if (twilioBalance) {
      metrics.push(`Twilio — Balance ${twilioBalance}`);
    }
  }
  if (isAiApiHit(currentSuperHit)) {
    metrics.push('AI API — valid key');
  }
  getEliteAiModels(currentSuperHit).forEach((model) => {
    metrics.push(`Premium model — ${model.label}`);
  });
  if (hasBedrockAccess(currentSuperHit)) {
    metrics.push('AWS Bedrock — invoke permissions detected');
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="sh-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="super-hit-title"
      >
        <div className="sh-backdrop" onClick={closeSuperHitPopup} aria-hidden="true" />
        <motion.div
          initial={{ y: 24, scale: 0.92, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 16, scale: 0.94, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="sh-panel"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <div className="sh-panel-glow" aria-hidden="true" />
          {/* Particle burst — 8 particles radiating from icon position */}
          <div className="sh-particles" aria-hidden="true">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="sh-particle" />
            ))}
          </div>
          <button type="button" onClick={closeSuperHitPopup} className="sh-close" aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            </svg>
          </button>

          <div className="sh-body">
            <div className="sh-icon-wrap" aria-hidden="true">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path
                  d="M16 4l2.5 6 6.5.6-4.8 4.2 1.4 6.2L16 18l-5.6 3-1.4-6.2-4.8-4.2 6.5-.6L16 4z"
                  stroke="#e53935"
                  strokeWidth="1.25"
                  fill="rgba(229, 57, 53,0.15)"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h2 id="super-hit-title" className="sh-title">
              Super hit detected
            </h2>
            <p className="sh-url">{currentSuperHit.url}</p>

            {metrics.length > 0 && (
              <div className="sh-metrics">
                <p className="sh-metrics-title">Credentials exceptionnels</p>
                {metrics.map((line) => (
                  <div key={line} className="sh-metric-row">
                    <span className="sh-metric-dot" />
                    <span>{line}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="sh-services">
              {currentSuperHit.services.map((service) => (
                <span key={service} className="sh-service-tag">
                  {service}
                </span>
              ))}
              {getEliteAiModels(currentSuperHit).map((model) => (
                <span key={model.id} className="sh-service-tag sh-service-tag--elite">
                  {model.label.toUpperCase()}
                </span>
              ))}
              {hasBedrockAccess(currentSuperHit) && (
                <span className="sh-service-tag sh-service-tag--bedrock">BEDROCK</span>
              )}
            </div>

            <button type="button" onClick={closeSuperHitPopup} className="sh-cta">
              Continue l&apos;analyse
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SuperHitPopup;
